import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  heroData,
  capabilities,
  industries,
  differentiators,
  outcomes,
} from "../data/mock";
import {
  ArrowRight,
  FileCode2,
  Boxes,
  ShieldCheck,
  Workflow,
  Dices,
  BatteryCharging,
  Radar,
} from "lucide-react";
import { TechStackGrid } from "../components/brand/TechLogos";
import { NetworkMesh } from "../components/animations/NetworkMesh";
import { Reveal, RevealGroup } from "../components/animations/Reveal";
import { CountUp } from "../components/animations/CountUp";
import { cn } from "@/lib/utils";

const capabilityIcons = { FileCode2, Boxes, ShieldCheck, Workflow };
const industryIcons = { Dices, BatteryCharging, Radar };

const accentClasses = {
  igaming: {
    text: "text-[hsl(var(--igaming))]",
    bg: "bg-[hsl(var(--igaming))]/10",
    border: "hover:border-[hsl(var(--igaming))]/50",
    bar: "from-[hsl(var(--igaming))] to-[hsl(var(--igaming-2))]",
  },
  battery: {
    text: "text-[hsl(var(--battery))]",
    bg: "bg-[hsl(var(--battery))]/10",
    border: "hover:border-[hsl(var(--battery))]/50",
    bar: "from-[hsl(var(--battery))] to-[hsl(var(--battery-2))]",
  },
  automotive: {
    text: "text-[hsl(var(--automotive))]",
    bg: "bg-[hsl(var(--automotive))]/10",
    border: "hover:border-[hsl(var(--automotive))]/50",
    bar: "from-[hsl(var(--automotive))] to-[hsl(var(--automotive-2))]",
  },
};

export default function Home() {
  return (
    <div className="bg-background">
      {/* Hero Section */}
      <section className="relative overflow-hidden border-b border-border scanline">
        <div className="absolute inset-0 graph-bg" />
        <NetworkMesh
          className="absolute inset-0 w-full h-full opacity-40"
          nodeColor="20, 184, 166"
          lineColor="20, 184, 166"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/60 to-background" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
          <div className="max-w-4xl">
            <Reveal className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary border border-border text-primary text-xs font-mono uppercase tracking-wider mb-8">
              <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
              {heroData.eyebrow}
            </Reveal>
            <Reveal delay={80}>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-foreground leading-tight mb-6">
                {heroData.title}
              </h1>
            </Reveal>
            <Reveal delay={160}>
              <p className="text-lg lg:text-xl text-muted-foreground leading-relaxed mb-10 max-w-3xl">
                {heroData.subtitle}
              </p>
            </Reveal>
            <Reveal delay={240} className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button
                  size="lg"
                  className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold px-8 h-12 text-base w-full sm:w-auto"
                >
                  {heroData.primaryCta}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/capabilities">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-border text-foreground hover:bg-secondary hover:text-foreground px-8 h-12 text-base w-full sm:w-auto"
                >
                  {heroData.secondaryCta}
                </Button>
              </Link>
            </Reveal>
          </div>

          {/* Capability tag strip */}
          <Reveal delay={320} className="mt-16 flex flex-wrap gap-x-8 gap-y-3 font-mono text-xs uppercase tracking-wider text-muted-foreground">
            {["Infrastructure as Code", "Container Orchestration", "Cybersecurity", "AI Workflow Automation (n8n)"].map((tag, i) => (
              <span key={tag} className="flex items-center gap-2">
                {i > 0 && <span className="text-border">/</span>}
                {tag}
              </span>
            ))}
          </Reveal>
        </div>
      </section>

      {/* Who We Serve */}
      <section id="industries" className="py-20 lg:py-28 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="max-w-2xl mb-16">
            <p className="text-primary text-sm font-mono uppercase tracking-wider mb-3">Who We Serve</p>
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              Three Industries. One Engineering Discipline.
            </h2>
            <p className="text-muted-foreground text-lg">
              Different compliance regimes, different threat models, different scale patterns —
              built on the same foundation of IaC, containers, security, and targeted automation.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {industries.map((ind, i) => {
              const Icon = industryIcons[ind.icon] || Dices;
              const accent = accentClasses[ind.accent];
              return (
                <Reveal key={ind.slug} delay={i * 120}>
                  <Link
                    to={`/industries/${ind.slug}`}
                    className={cn(
                      "group flex flex-col h-full rounded-2xl bg-card border border-border overflow-hidden transition-all duration-300 hover:shadow-xl hover:shadow-black/20",
                      accent.border
                    )}
                  >
                    <div className={cn("h-1 w-full bg-gradient-to-r", accent.bar)} />
                    <div className="p-8 flex flex-col flex-1">
                      <div className={cn("w-12 h-12 rounded-xl flex items-center justify-center mb-6", accent.bg)}>
                        <Icon className={cn("w-6 h-6", accent.text)} />
                      </div>
                      <h3 className="text-xl font-semibold text-foreground mb-2">{ind.fullName}</h3>
                      <p className="text-muted-foreground text-sm leading-relaxed mb-6">{ind.tagline}</p>

                      <div className="flex flex-wrap gap-2 mb-6">
                        {ind.pipeline.slice(0, 3).map((p) => (
                          <span
                            key={p.step}
                            className="px-2.5 py-1 rounded-md bg-secondary text-secondary-foreground text-xs font-mono"
                          >
                            {p.step}
                          </span>
                        ))}
                      </div>

                      <div className="mt-auto flex items-center text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                        Explore {ind.name}
                        <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Capability Pillars */}
      <section id="capabilities" className="py-20 lg:py-28 bg-card/40 border-y border-border scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="max-w-2xl mb-16">
            <p className="text-primary text-sm font-mono uppercase tracking-wider mb-3">Core Capabilities</p>
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              What We Actually Build
            </h2>
            <p className="text-muted-foreground text-lg">
              The four disciplines behind every engagement — applied to your industry's specific data, not a generic template.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {capabilities.map((cap, i) => {
              const Icon = capabilityIcons[cap.icon] || FileCode2;
              return (
                <Reveal key={cap.id} delay={i * 100}>
                  <div className="group h-full p-8 rounded-2xl bg-card border border-border hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5 transition-all duration-300">
                    <div className="flex items-start justify-between mb-6">
                      <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/15 transition-colors">
                        <Icon className="w-7 h-7 text-primary" />
                      </div>
                    </div>
                    <h3 className="text-xl font-semibold text-foreground mb-3">{cap.title}</h3>
                    <p className="text-muted-foreground leading-relaxed mb-6">{cap.description}</p>
                    <ul className="space-y-2 mb-6">
                      {cap.features.slice(0, 3).map((f) => (
                        <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <span className="mt-1.5 w-1 h-1 rounded-full bg-primary flex-shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>
                    <Link
                      to={`/capabilities#${cap.slug}`}
                      className="inline-flex items-center text-primary text-sm font-medium hover:text-primary/80 transition-colors"
                    >
                      Learn more
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Link>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Differentiators */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">Why Grafodata</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Depth over breadth — the same principle applied across every engagement
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {differentiators.map((diff, index) => (
              <Reveal key={diff.id} delay={index * 100} className="flex gap-6 p-6 rounded-2xl bg-card border border-border">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <span className="text-primary font-mono font-bold">{String(index + 1).padStart(2, "0")}</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">{diff.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{diff.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="py-20 lg:py-28 bg-card/40 border-y border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">What You Get</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              A foundation built for regulated, high-volume, and OT-adjacent environments
            </p>
          </Reveal>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {outcomes.map((outcome, i) => (
              <Reveal key={outcome.id} delay={i * 90} className="text-center p-8 rounded-2xl bg-card border border-border">
                <div className="text-3xl lg:text-4xl font-bold text-primary mb-3 font-mono">
                  <CountUp value={outcome.metric} />
                </div>
                <p className="text-muted-foreground text-sm">{outcome.label}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="text-center mb-12">
            <p className="text-muted-foreground text-sm font-mono uppercase tracking-wider mb-2">
              Platform Expertise
            </p>
            <h2 className="text-2xl lg:text-3xl font-bold text-foreground">
              Multi-Cloud, Container & Automation Stack
            </h2>
          </Reveal>
          <Reveal delay={100}>
            <TechStackGrid />
          </Reveal>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="relative overflow-hidden rounded-3xl bg-card border border-border p-12 lg:p-16">
            <div className="absolute inset-0 graph-bg-dense opacity-60" />
            <NetworkMesh className="absolute inset-0 w-full h-full opacity-30" nodeColor="20, 184, 166" lineColor="20, 184, 166" />
            <div className="absolute inset-0 bg-gradient-to-br from-background/10 via-transparent to-background/60" />

            <div className="relative flex flex-col lg:flex-row items-center justify-between gap-10">
              <div className="max-w-2xl text-center lg:text-left">
                <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
                  Ready to Build the Infrastructure Layer?
                </h2>
                <p className="text-muted-foreground text-lg">
                  Tell us about your SCADA, ADAS/AV, or iGaming data challenge — we'll scope the
                  architecture, not pitch a generic cloud audit.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 flex-shrink-0">
                <Link to="/contact">
                  <Button
                    size="lg"
                    className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold px-8 h-12 w-full sm:w-auto"
                  >
                    Talk to an Architect
                  </Button>
                </Link>
                <Link to="/industries">
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-border text-foreground hover:bg-secondary px-8 h-12 w-full sm:w-auto"
                  >
                    Explore Industries
                  </Button>
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
